import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Toasts,
  Form,
  Modal,
  Box,
  Heading,
  Level,
  Button,
  Text,
  Disclosure,
  Icon,
  Price,
  Columns,
  Clipped,
  Input,
} from '@deseretbook/react-ui'
import WalletPaymentSourcesFormField from '../../checkout/payment/WalletPaymentSourcesFormField'
import { Slices } from '../../prismic'
import SubscriptionPlanFormField from './SubscriptionPlanFormField'
import MembershipAddonsFormField from './MembershipAddonsFormField'
import ShippingAddressFormField from './ShippingAddressFormField'
import { SIGNUP_VIEWS, SubscriptionPriceSummary } from './'
import PaymentSourceDetails from '../../checkout/payment/PaymentSourceDetails'


/**
 * The main form view of the subscriptions signup modal.
 *
 * @returns {function} Component
 */
const FormView = ({
  subscriptionForm,
  changeView,
  detailsDocument,
  user,
}) => {
  const { data: displayDetails } = detailsDocument
  const { selectedEstimate, userCanCreate } = subscriptionForm

  if (!userCanCreate) {
    changeView(SIGNUP_VIEWS.details, false)

    return false
  }

  const displayPrices = [
    selectedEstimate
      && (new Date(`${selectedEstimate.firstBillingDate} 00:00:00`).toDateString() === new Date().toDateString())
      && +selectedEstimate.nextBillingPeriodAmount,
    ...subscriptionForm.form.addonSubscriptions
      .filter(addonPlanId => (subscriptionForm.estimates[addonPlanId] && (
        new Date(`${subscriptionForm.estimates[addonPlanId].firstBillingDate} 00:00:00`)
          .toDateString() === new Date().toDateString())
      ))
      .map(addonPlanId => +subscriptionForm.estimates[addonPlanId].nextBillingPeriodAmount),
  ]
    .filter(Boolean)

  const selectedEstimateIsPlatinum = selectedEstimate
    && selectedEstimate.subscriptionPlan
    && subscriptionForm.platinumMembershipPlan
    && subscriptionForm.platinumMembershipPlan.btPlanId
      === selectedEstimate.subscriptionPlan.btPlanId
  const addonsContainPlatinum = !!(
    subscriptionForm.platinumMembershipPlan
    && subscriptionForm.form.addonSubscriptions
      .find(btPlanId => btPlanId === subscriptionForm.platinumMembershipPlan.btPlanId)
  )
  const addonCount = subscriptionForm.form.addonSubscriptions.length
  const platinumMembershipName = (subscriptionForm.platinumMembershipPlan
    && subscriptionForm.platinumMembershipPlan.name) || 'Platinum Membership'

  /**
   * Submits the form if the user presses Enter.
   * @param {object} event - The event object
   */
  const submitForm = (event) => {
    if (subscriptionForm.userCanCreate && event.key === "Enter") {
      subscriptionForm.createSubscription()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', submitForm)

    return () => {
      document.removeEventListener('keydown', submitForm)
    }
  }, [])

  return (
  <React.Fragment>
    <Modal.Content isPaddingless>
      <Form
        id="signup-form"
        onSubmit={subscriptionForm.createSubscription}
        isSubmitting={subscriptionForm.isCreating}
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Marketing Details */}
        {!!(displayDetails && displayDetails.formSlices.length) && (
          <Box padding={1}>
            <Slices slices={displayDetails.formSlices} user={user} />
          </Box>
        )}

        {/* Subscription Plan */}
        <Box padding={1}>
          <Heading>Subscription Plan</Heading>
          <SubscriptionPlanFormField
            subscriptionForm={subscriptionForm}
            detailsDocument={detailsDocument}
          />
        </Box>

        {/* Membership Addons */}
        <MembershipAddonsFormField subscriptionForm={subscriptionForm} />

        {/* Payment Method */}
        <Box padding={1}>
          <Heading>Payment Method</Heading>
          <WalletPaymentSourcesFormField
            name="walletSourceId"
            onChange={subscriptionForm.onFormChange}
            sources={subscriptionForm.paymentSources}
            value={subscriptionForm.form.walletSourceId}
            disabled={subscriptionForm.isLoading}
          />
          <Level>
            <Level.Left>
              <Level.Item>
                <Button
                  type="button"
                  onClick={() => changeView(SIGNUP_VIEWS.cardForm)}
                  disabled={subscriptionForm.isLoading}
                >
                  Add New Card
                </Button>
              </Level.Item>
            </Level.Left>
          </Level>
          <Box maxWidth={20} paddingTop={2}>
            <Disclosure disabled={subscriptionForm.isLoading}>
              <Disclosure.Header>Apply gift card</Disclosure.Header>
              <Disclosure.Content>
                {subscriptionForm.giftCard && (
                  <Box paddingBottom={0.5}>
                    <Level>
                      <Level.Left>
                        <Level.Item>
                          <PaymentSourceDetails
                            source={subscriptionForm.giftCard}
                            amount={
                              (selectedEstimateIsPlatinum && !addonCount)
                                ? Math.min(25, subscriptionForm.giftCard.balance)
                                : subscriptionForm.giftCard.balance
                            }
                          />
                        </Level.Item>
                      </Level.Left>
                      <Level.Right>
                        <Level.Item>
                          <Button
                            type="button"
                            disabled={subscriptionForm.isLoading}
                            onClick={() => {
                              subscriptionForm.removeGiftCard()
                              Toasts.success('Gift card removed')
                            }}
                            aria-label="Remove gift card"
                          >
                            Remove
                          </Button>
                        </Level.Item>
                      </Level.Right>
                    </Level>
                  </Box>
                )}
                <Input
                  name="number"
                  type="text"
                  label="Gift Card Number"
                  value={subscriptionForm.giftCardForm.number}
                  onChange={subscriptionForm.onGiftCardFormChange}
                  disabled={subscriptionForm.isLoading}
                />
                <Level>
                  <Level.Right>
                    <Button
                      color="primary"
                      isLoading={subscriptionForm.isFetchingGiftCard}
                      disabled={
                        subscriptionForm.isLoading || !subscriptionForm.giftCardForm.number.length
                      }
                      onClick={() => {
                        subscriptionForm.applyGiftCard(
                          ({ balance }) => {
                            const displayAmount = (selectedEstimateIsPlatinum && !addonCount)
                              ? Math.min(25, balance)
                              : balance

                            Toasts.success('Gift card applied', `$${displayAmount} will be applied to your subscription`)
                          },
                          error => Toasts.danger('Unable to apply gift card', String(error)),
                        )
                      }}
                    >
                      Apply Gift Card
                    </Button>
                  </Level.Right>
                </Level>
              </Disclosure.Content>
            </Disclosure>
          </Box>
        </Box>

        {/* Delivery Address */}
        {!!subscriptionForm.requiresPhysicalAddress && (
          <Box padding={1}>
            <Heading>Delivery Address</Heading>

            <ShippingAddressFormField subscriptionForm={subscriptionForm} />
            <Level>
              <Level.Left>
                <Level.Item>
                  <Button
                    type="button"
                    onClick={() => changeView(SIGNUP_VIEWS.addressForm)}
                    disabled={subscriptionForm.isLoading}
                  >
                    Add New Address
                  </Button>
                </Level.Item>
              </Level.Left>
            </Level>
          </Box>
        )}

        <Box padding={1}>
          <Disclosure isDisabled={subscriptionForm.isLoading}>
            <Disclosure.Header>
              Pricing Summary
            </Disclosure.Header>
            <Disclosure.Content>
              <Box paddingBottom={1}>
                <Columns>
                  {selectedEstimate && (
                    <Columns.Column>
                      <Box maxWidth={20} width="100%">
                        <SubscriptionPriceSummary
                          subscription={selectedEstimate}
                        />
                      </Box>
                    </Columns.Column>
                  )}
                  {subscriptionForm.form.addonSubscriptions.map(addonPlanId => (
                    <Columns.Column key={addonPlanId}>
                      <Box maxWidth={20} width="100%">
                        <SubscriptionPriceSummary
                          subscription={subscriptionForm.estimates[addonPlanId]}
                        />
                      </Box>
                    </Columns.Column>
                  ))}
                </Columns>
                {!!(subscriptionForm.giftCard && (
                  (selectedEstimateIsPlatinum && !addonCount)
                  || (addonsContainPlatinum && subscriptionForm.giftCard.balance <= 25)
                )) && (
                  <Text display="block" size="tiny">
                    <Price value={Math.min(25, subscriptionForm.giftCard.balance)} /> from your
                    gift card will be applied to your {platinumMembershipName} subscription.
                  </Text>
                )}
                {!!(subscriptionForm.giftCard && !selectedEstimateIsPlatinum && !addonCount) && (
                  <Text display="block" size="tiny">
                    <Price value={subscriptionForm.giftCard.balance} /> from your
                    gift card will be applied to your subscription.
                  </Text>
                )}
                {!!(
                  addonsContainPlatinum
                  && subscriptionForm.giftCard
                  && subscriptionForm.giftCard.balance > 25
                ) && (
                  <Text display="block" size="tiny">
                    <Price value={25} /> from your gift card will be applied to
                    your {platinumMembershipName} subscription.
                    The remaining <Price value={subscriptionForm.giftCard.balance - 25} /> will be
                    applied to your {(selectedEstimate && selectedEstimate.subscriptionPlan.name) || 'subscription'}.
                  </Text>
                )}
              </Box>
            </Disclosure.Content>
          </Disclosure>
          </Box>
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <Level>
          {!!(displayDetails && (displayDetails.detailsSlices || []).length) && (
            <Level.Left>
              <Level.Item>
                <Button
                  type="button"
                  color="light"
                  onClick={() => changeView('details')}
                  disabled={subscriptionForm.isLoading}
                >
                  Learn More
                </Button>
              </Level.Item>
            </Level.Left>
          )}
          <Level.Right>
            {(subscriptionForm.isLoading && !subscriptionForm.isCreating) && (
              <Level.Item>
                <Icon name="loading" isSpinning />
              </Level.Item>
            )}
            {(!!displayPrices.length
              && !(subscriptionForm.isLoading && !subscriptionForm.isCreating))
              && (
              <Level.Item>
                <Clipped>
                  <Text hasEllipsis size="tiny">Estimated Total</Text>
                  <Text hasEllipsis>
                    {displayPrices
                      .map((price, idx) => (
                        <React.Fragment key={price}>
                          {idx > 0 && <Text> + </Text>}
                          <Price value={price} />
                        </React.Fragment>
                      ))}
                  </Text>
                </Clipped>
              </Level.Item>
            )}
            <Level.Item>
              {subscriptionForm.userCanCreate && (
                <Button
                  type="submit"
                  form="signup-form"
                  color="action"
                  isLoading={subscriptionForm.isLoading || !selectedEstimate}
                  disabled={subscriptionForm.isLoading || !selectedEstimate}
                >
                  Sign Up Now
                </Button>
                )}
            </Level.Item>
          </Level.Right>
        </Level>
      </Modal.Footer>
    </React.Fragment>
  )
}

FormView.propTypes = {
  subscriptionForm: PropTypes.shape({
    platinumDiscount: PropTypes.number,
    selectedEstimate: PropTypes.shape({}),
    form: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string),
    ])),
    onFormChange: PropTypes.func,
  }).isRequired,
  detailsDocument: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  user: PropTypes.shape({
    id: PropTypes.number,
  }),
  changeView: PropTypes.func,
}

FormView.defaultProps = {
  detailsDocument: {
    data: {},
  },
  user: {},
  changeView: () => {},
}

export default FormView
